import { useSessionStore } from './store/sessionStore';
import { useUIStore } from './store/uiStore';
import { getProviders } from './providers/registry';
import { MockAdapter } from './providers/MockAdapter';
import type { Message, StreamEvent } from './agents/types';
import { shortId } from './utils';

interface RunHandle {
  controller: AbortController;
  promise: Promise<void>;
}

// Active streams keyed by sessionId so Stop can target the right one.
const active = new Map<string, RunHandle>();

export function isStreaming(sessionId: string): boolean {
  return active.has(sessionId);
}

export function cancelStream(sessionId: string): void {
  active.get(sessionId)?.controller.abort();
}

export function runStream(sessionId: string): RunHandle {
  // cancel any prior stream on this session
  cancelStream(sessionId);

  const controller = new AbortController();
  const promise = (async () => {
    const store = useSessionStore.getState();
    const session = store.sessions.find((s) => s.id === sessionId);
    if (!session) return;

    // pick the adapter — fall back to MockAdapter for any unconfigured provider
    const provider = getProviders().find((p) => p.id === session.providerId);
    const adapter = provider?.adapter ?? MockAdapter;

    // assistant message — created on first message-start so we know its id
    let assistantId: string | null = null;

    try {
      const iter = adapter.stream({
        sessionId,
        modelId: session.modelId,
        effort: session.effort,
        messages: session.messages,
        signal: controller.signal,
      });

      for await (const ev of iter) {
        if (controller.signal.aborted) break;
        applyEvent(sessionId, ev, (id) => { assistantId = id; });
      }

      // ensure we don't leave the session in an active state
      const cur = useSessionStore.getState().sessions.find((s) => s.id === sessionId);
      if (cur && ['thinking', 'streaming', 'tool_calling'].includes(cur.status)) {
        useSessionStore.getState().setStatus(sessionId, 'idle');
      }
    } catch (err: unknown) {
      const isAbort = (err as { name?: string })?.name === 'AbortError';
      if (!isAbort) {
        // surface the error as a system message
        useSessionStore.getState().appendMessage(sessionId, {
          id: shortId('msg'),
          role: 'system',
          createdAt: Date.now(),
          parts: [
            {
              type: 'text',
              text: `Stream error: ${(err as Error)?.message ?? String(err)}`,
            },
          ],
        });
      }
      useSessionStore.getState().setStatus(sessionId, isAbort ? 'idle' : 'error');
    } finally {
      active.delete(sessionId);
      // mark message ended cleanly so streaming dot stops
      // (status was already set above)
      void assistantId;
    }
  })();

  active.set(sessionId, { controller, promise });
  return { controller, promise };
}

function applyEvent(
  sessionId: string,
  ev: StreamEvent,
  onMessageStart: (id: string) => void
) {
  const s = useSessionStore.getState();

  // Panel-update events steer Inspector content; not tied to a message.
  if (ev.type === 'panel-update') {
    if (ev.panel === 'preview') {
      s.setPanelState(sessionId, { preview: ev.data as { url: string; title?: string } });
    } else if (ev.panel === 'plan') {
      s.setPanelState(sessionId, { plan: ev.data as { markdown: string } });
    }
    // Per UX spec: when claude updates a panel, if no view is open we
    // open the inspector + switch to that mode; if a view is already open
    // we switch to the matching mode. Only does this for the active session.
    if (sessionId === s.activeId) {
      const ui = useUIStore.getState();
      if (ui.mode !== ev.panel) ui.setMode(ev.panel);
      if (!ui.inspectorOpen) ui.setInspectorOpen(true);
    }
    return;
  }

  switch (ev.type) {
    case 'message-start': {
      onMessageStart(ev.messageId);
      const stub: Message = {
        id: ev.messageId,
        role: ev.role,
        createdAt: Date.now(),
        parts: [],
        // record current model/effort on the assistant message for later inspection
        model: s.sessions.find((x) => x.id === sessionId)?.modelId,
        effort: s.sessions.find((x) => x.id === sessionId)?.effort,
      };
      s.appendMessage(sessionId, stub);
      return;
    }
    case 'reasoning-delta':
      s.appendReasoningDelta(sessionId, ev.messageId, ev.delta);
      return;
    case 'text-delta':
      s.appendTextDelta(sessionId, ev.messageId, ev.delta);
      return;
    case 'tool-call':
      s.appendToolCallPart(sessionId, ev.messageId, ev.toolCall);
      return;
    case 'tool-update':
      s.updateToolCall(sessionId, ev.messageId, ev.toolCallId, ev.patch);
      return;
    case 'status':
      s.setStatus(sessionId, ev.status);
      return;
    case 'message-end':
      if (ev.trailingStatus) s.setTrailingStatus(sessionId, ev.messageId, ev.trailingStatus);
      s.setStatus(sessionId, 'idle');
      return;
    case 'error':
      s.setStatus(sessionId, 'error');
      return;
  }
}
